import {
  DOMUtil,
} from '../rocket';

export interface DOMTraverseInspectFunction {
  (element: HTMLElement): true | void;
}

export interface DOMTraverseIdentifyElementFunction {
  (element: HTMLElement): boolean;
}

export interface DOMTraverseExtractFunction<T> {
  (child: HTMLElement): T | void;
}

export type DOMTraverseResult = HTMLElement | HTMLElement[] | false;

export class DOMTraverse {

  public static ascendFrom(element: HTMLElement, inspect: DOMTraverseInspectFunction): void {
    let currentElement: HTMLElement | null = element;

    while (
      currentElement !== null
      && currentElement !== document.documentElement
    ) {
      currentElement = currentElement as HTMLElement;

      if (currentElement !== null) {
        if (inspect(currentElement) === true) {
          break;
        } else {
          currentElement = currentElement.parentElement;
        }
      }
    }
  }

  public static descendFrom(element: HTMLElement, inspect: DOMTraverseInspectFunction): void {
    const descent = (currentElement: HTMLElement) => {
      const children = currentElement.children;

      if (children.length > 0) {
        for (let i = 0; i < children.length; i++) {
          if (inspect(children[i] as HTMLElement) === true) {
            break;
          } else if (children[i].children.length > 0) {
            descent(children[i] as HTMLElement);
          }
        }
      }
    };

    descent(element);
  }

  public static findAncestor(
    element: HTMLElement,
    identifyElement: DOMTraverseIdentifyElementFunction,
    getAll: boolean = false,
  ): DOMTraverseResult {
    const results: HTMLElement[] = [];

    if (DOMUtil.isHTMLElement(element) === false) {
      return false;
    }

    if (identifyElement(element) === true) {
      results.push(element);
    }

    let currentElement: HTMLElement | null = element;

    while (
      currentElement !== null
      && currentElement !== document.documentElement
    ) {
      currentElement = currentElement as HTMLElement;

      if (currentElement !== null) {
        if (identifyElement(currentElement) === true) {
          results.push(currentElement);

          if (getAll === false) {
            break;
          }
        }

        currentElement = currentElement.parentElement;
      }
    }

    if (results.length > 0) {
      return getAll === true ? results : results[0];
    }

    return false;
  }

  public static findDescendant(
    element: HTMLElement,
    identifyElement: DOMTraverseIdentifyElementFunction,
    getAll: boolean = false,
  ): DOMTraverseResult {

    const results: HTMLElement[] = [];

    if (identifyElement(element) === true) {
      results.push(element);
    }

    const descent = (currentElement: HTMLElement) => {
      const children = currentElement.children;

      if (children.length > 0) {
        for (let i = 0; i < children.length; i++) {
          if (identifyElement(children[i] as HTMLElement) === true) {
            results.push(children[i] as HTMLElement);

            if (getAll === false) {
              break;
            }
          }

          if (children[i].children.length > 0) {
            descent(children[i] as HTMLElement);
          }
        }
      }
    };

    descent(element);

    if (results.length > 0) {
      return getAll === true ? results : results[0];
    }

    return false;
  }

  public static findAncestorWithClass(
    from: HTMLElement,
    classNames: string | string[],
    getAll: boolean = false,
  ): DOMTraverseResult {
    let identifyElement;

    if (typeof classNames === 'string') {
      identifyElement = element => element.classList.contains(classNames);
    } else if (Array.isArray(classNames) === true) {
      identifyElement = element => {
        for (let i = 0; i < classNames.length; i++) {
          if (element.classList.contains(classNames[i]) === true) {
            return true;
          }
        }

        return false;
      };
    }

    return this.findAncestor(from, identifyElement, getAll);
  }

  public static findDescendantWithClass(
    parent: HTMLElement,
    classNames: string | string[],
    getAll: boolean = false,
  ): DOMTraverseResult {
    let identifyElement;

    if (typeof classNames === 'string') {
      identifyElement = element => element.classList.contains(classNames);
    } else if (Array.isArray(classNames) === true) {
      identifyElement = element => {
        for (let i = 0; i < classNames.length; i++) {
          if (element.classList.contains(classNames[i]) === true) {
            return true;
          }
        }

        return false;
      };
    }

    return this.findDescendant(parent, identifyElement, getAll);
  }

  public static findAncestorWithId(
    from: HTMLElement,
    id: string,
    getAll: boolean = false,
  ): DOMTraverseResult {
    const identifyElement = element => element.id === id;

    return this.findAncestor(from, identifyElement, getAll);
  }

  public static findDescendantWithid(
    from: HTMLElement,
    id: string,
    getAll: boolean = false,
  ): DOMTraverseResult {
    const identifyElement = element => element.id === id;

    return this.findDescendant(from, identifyElement, getAll);
  }

  public static hasAncestor(
    from: HTMLElement,
    options: HTMLElement | HTMLElement[] | NodeListOf<HTMLElement>,
  ): DOMTraverseResult {
    const identifyElement = element => {
      if (
        Array.isArray(options) === true
        && DOMUtil.isHTMLElement(...options as HTMLElement[]) === true
      ) {
        options = options as HTMLElement[];

        return options.indexOf(element) !== -1;
      } else if (
        DOMUtil.isNodeListOfHTMLElement(options) === true
      ) {
        options = options as NodeListOf<HTMLElement>;

        return [...options].indexOf(element) !== -1;
      } else {
        return element === options;
      }
    };

    return this.findAncestor(from, identifyElement, false);
  }

  public static hasDescendant(
    parent: HTMLElement,
    options: HTMLElement | HTMLElement[] | NodeListOf<HTMLElement>,
  ): DOMTraverseResult {
    const identifyElement: DOMTraverseIdentifyElementFunction = element => {
      if (
        Array.isArray(options) === true
        && DOMUtil.isHTMLElement(...options as HTMLElement[]) === true
      ) {
        options = options as HTMLElement[];

        return options.indexOf(element) !== -1;
      } else if (
        DOMUtil.isNodeListOfHTMLElement(options) === true
      ) {
        options = options as NodeListOf<HTMLElement>;
        
        return [...options].indexOf(element) !== -1;
      } else {
        return element === options;
      }
    };

    return this.findDescendant(parent, identifyElement, false);
  }

  // @siblings

  public static getSiblings(
    element: HTMLElement,
    isExclusive: boolean = false,
  ): HTMLElement[] | false {
    if (element.parentElement !== null) {
      const siblings = [...element.parentElement.children] as HTMLElement[];

      if (isExclusive === true) {
        siblings.splice(siblings.indexOf(element), 1);
      }

      return siblings.length > 0 ? siblings : false;
    }

    return false;
  }

  public static findSibling(
    element: HTMLElement,
    identifyElement: DOMTraverseIdentifyElementFunction,
    getAll = true,
  ): DOMTraverseResult {
    const siblings: HTMLElement[] | false = this.getSiblings(element);

    if (siblings === false) {
      return false;
    }

    if (siblings.length > 0) {
      const results: HTMLElement[] = [];

      for (let i = 0; i < siblings.length; i++) {
        if (identifyElement(siblings[i]) === true) {
          results.push(siblings[i]);
        }
      }

      if (results.length > 0) {
        return getAll === true ? results : results[0];
      }
    }

    return false;
  }

  public static findSiblingWithClass(
    element: HTMLElement,
    classNames: string | string[],
    getAll: boolean = false,
  ): DOMTraverseResult {
    let identifyElement;

    if (typeof classNames === 'string') {
      identifyElement = sibling => sibling.classList.contains(classNames);
    } else if (Array.isArray(classNames) === true) {
      identifyElement = sibling => {
        for (let i = 0; i < classNames.length; i++) {
          if (sibling.classList.contains(classNames[i]) === true) {
            return true;
          }
        }

        return false;
      };
    }

    return this.findSibling(element, identifyElement, getAll);
  }

  public static removeChildren(parent: HTMLElement): number {
    let deleteCount = 0;

    while (parent.firstChild !== null) {
      parent.removeChild(parent.firstChild);

      deleteCount++;
    }

    return deleteCount;
  }

  public static findNextSibling(
    element: HTMLElement,
    identifyElement: DOMTraverseIdentifyElementFunction,
  ): HTMLElement | false {
    let nextSibling: HTMLElement | null = element;

    while (nextSibling !== null) {
      if (element !== null && identifyElement(element) === true) {
        return element;
      } else {
        nextSibling = element.nextElementSibling as HTMLElement | null;
      }
    }

    return false;
  }

  public static removeChild(
    element: HTMLElement,
    identifyElement: DOMTraverseIdentifyElementFunction,
  ): number {
    let deleteCount = 0;

    const inspect = parent => {
      const children = parent.children;

      if (children.length > 0) {
        for (let i = 0; i < children.length; i++) {
          if (identifyElement(children[i] as HTMLElement) === true) {
            parent.removeChild(children[i]);

            deleteCount++;
          } else if (children[i].children.length > 0) {
            inspect(children[i]);
          }
        }
      }
    };

    inspect(element);

    return deleteCount;
  }

  public static getChildren(
    parent: HTMLElement,
    identifyElement?: DOMTraverseIdentifyElementFunction,
  ): HTMLElement[] {
    const children = [...parent.children] as HTMLElement[];

    if (typeof identifyElement === 'undefined') {
      return children;
    }

    return children.filter(element => {
      return identifyElement(element as HTMLElement);
    });
  }

  public static getNthChild(
    n: number | 'last',
    parent: HTMLElement,
    identifyElement?: DOMTraverseIdentifyElementFunction,
  ): HTMLElement | false {
    if (typeof identifyElement === 'undefined') {
      identifyElement = element => true;
    }

    const children = [...parent.children] as HTMLElement[];

    const selectedChildren = children.filter(identifyElement);

    let result;

    if (n === 'last') {
      result = selectedChildren[selectedChildren.length - 1];
    } else {
      result = selectedChildren[n];
    }

    return typeof result === 'object' ? result : false;
  }

  public static mapDataFromChildren<T>(
    parent: HTMLElement,
    extract: DOMTraverseExtractFunction<T>,
    identifyElement?: DOMTraverseIdentifyElementFunction,
  ): T[] {
    if (typeof identifyElement === 'undefined') {
      identifyElement = element => true;
    }

    const children = [...parent.children] as HTMLElement[];

    const selectedChildren = children.filter(identifyElement);

    if (selectedChildren.length === 0) {
      return [];
    }

    if (selectedChildren.length === 1) {
      const datum: T = <T>extract(selectedChildren[0]);

      return typeof datum !== 'undefined' ? [datum] : [];
    }

    const results: T[] = [];

    selectedChildren.forEach(child => {
      const datum: T = <T>extract(child);

      if (typeof datum !== 'undefined') {
        results.push(datum);
      }
    });

    return results;
  }
}
